import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { Link } from '~spa/Components/Link/Link';
import {
    seoUrlEvent,
    seoUrlInplayEvent,
    seoUrlTournament,
    getBaseEventUrl,
} from '~spa/Utils/SeoUrls';
import EventSchedule from '~shared/components/react/EventSchedule';
import EventName from '~shared/components/react/EventName';
import EventScore from '~shared/components/react/EventScore';
import EventIcons from '~shared/components/react/EventIcons';
import EventStatusInplay from '~shared/components/react/EventStatusInplay';
import { extractHomeAwayScores } from '~shared/components/score-board/utils';
import NormalizeStrategies from '~shared/components/score-board/normalizers';
import {
    I18N_EVENT_DETAILS_URL_HOMEPAGE,
    I18N_EVENT_DETAILS_URL_EVENT,
} from '~shared/components/sport-event/constants';
import {
    GOLF_SLUG,
    HORSE_RACING_SLUG,
    GREYHOUND_RACING_SLUG,
    TROTTING_SLUG,
} from '~shared/utils/constants';
import '~shared/components/react/Scoreboard/styles/scoreboard-wrapper.scss';
import './styles.scss';

const EventCard = ({ event, gtm }) => {
    const [ t ] = useTranslation();
    const {
        id: eventId,
        event_type: eventType = '',
        inplay = false,
        isUsFormat = false,
        is_streaming_available: isStreamingAvailable = false,
        each_way_plus: isEachWayPlus = false,
        tournament_id: tournamentId,
        tournament_logos: tournamentLogos = {},
        competitors: _competitors = [],
        start_time: startTime,
        name,
        hasBoostedOddsSelections,
        is_standard_sport: isStandardSport,
        hide_countdown_timer : hideCountdownTimer,
        racing_name,
        extra_info,
        sport_slug: sportSlug,
        sport_l10n_slug,
        category_slug: categorySlug,
        category_l10n_slug,
        tournament_slug,
        tournament_l10n_slug,
        slug,
        event_l10n_slug,
        odds_boost_count: oddsBoostCount,
        is_odds_boost: isOddsBoost,
    } = event;
    const shouldShowTeamLogo = Boolean(tournamentLogos?.showTeamLogo);
    const shouldShowAwayJersey = Boolean(tournamentLogos?.showAwayJersey);
    const competitors = isEmpty(_competitors)
        ? null
        : Object.values(_competitors).sort(
            ({ order: orderA }, { order: orderB }) => orderA - orderB
        );
    const normalize = NormalizeStrategies[sportSlug];
    const { status = '', columns = [] } = (inplay && normalize?.(event)) || {};

    const renderEventName = (name, slug, isHome) => (
        <EventName
            shouldShowTeamLogo={shouldShowTeamLogo}
            shouldShowAwayJersey={shouldShowAwayJersey}
            categorySlug={categorySlug}
            name={name}
            sportSlug={sportSlug}
            slug={slug}
            isUsFormat={isUsFormat}
            isHome={isHome}
        />
    );
    const scores = extractHomeAwayScores(columns);

    const gridTemplate = classNames(
        {'min-content': shouldShowTeamLogo},
        '1fr',
        {[`repeat(${scores.home.length}, min-content)`]: scores.home.length},
    );

    const gridStyle = {
        gridTemplateColumns: gridTemplate,
    };

    const renderEventDescription = () => {
        if (!isStandardSport) {
            const displayName = racing_name ? racing_name : name;

            return renderEventName(displayName, categorySlug);
        }

        const events = competitors.map(({ slug, name }, index) => {
            const isHome = index === 0;

            const competitorScores = isHome
                ? scores.home
                : scores.away;

            return (
                <>
                    {renderEventName(name, slug, isHome)}
                    {!isEmpty(columns) && <EventScore scores={competitorScores} />}
                </>
            );
        });

        if (isUsFormat) {
            return events.reverse();
        }

        return events;
    };

    const renderEventStatus = () => (
        <div className={classNames({ 'event-card__status--inplay': inplay })}>
            {inplay ? (
                <EventStatusInplay
                    status={status}
                    isInplay={inplay}
                    extraInfo={extra_info}
                    categorySlug={categorySlug}
                    cssClasses="event-card__status--extra-info"
                />
            ) : (
                <EventSchedule
                    startTime={startTime}
                    isInplay={inplay}
                    hideCountdownTimer={hideCountdownTimer}
                    showDateInline
                    showGreyTimer
                />
            )}
        </div>
    );

    const renderEventIcons = () => (
        <EventIcons
            showBoostedIcons={hasBoostedOddsSelections}
            showEachWayPlus={isEachWayPlus}
            showStreaming={isStreamingAvailable}
            oddsBoostCount={oddsBoostCount}
            isOddsBoost={isOddsBoost}
        />
    );

    const buildUrl = () => {
        let url = null;
        const isOutright = eventType.toLowerCase() === 'outright';
        const urlSlugs = {
            sport_slug: sport_l10n_slug || sportSlug,
            category_slug: category_l10n_slug || categorySlug,
            tournament_slug: tournament_l10n_slug || tournament_slug,
            event_slug: isOutright ? t('url.outrights') : (event_l10n_slug || slug),
        };
        const isRacing = ([
            HORSE_RACING_SLUG,
            GREYHOUND_RACING_SLUG,
            TROTTING_SLUG,
        ].includes(sportSlug));
        const isGolf = sportSlug === GOLF_SLUG;

        if (isGolf) {
            url = seoUrlTournament(
                `${urlSlugs.sport_slug}/${urlSlugs.category_slug}/${urlSlugs.tournament_slug}`,
                tournamentId
            );
        } else if (inplay) {
            const inplayBaseUrl = `${t(I18N_EVENT_DETAILS_URL_HOMEPAGE)}/${t(I18N_EVENT_DETAILS_URL_EVENT)}`;

            url = seoUrlInplayEvent(`${inplayBaseUrl}/${eventId}`, eventId);
        } else {
            const baseUrl = getBaseEventUrl(urlSlugs);

            url = seoUrlEvent(baseUrl, eventId);
        }

        if (isRacing) {
            url += '?fromBetHistory';
        }

        return url;
    };

    return (

        <Link to={buildUrl()} gtmAttribute={gtm}>
            <div className={classNames('event-card', {'event-card--inplay': inplay})}>
                <div className="event-card__sports-logo">
                    <div
                        className={`bb-icon bb-icon--size-24x24 bb-icon--${sportSlug}`}
                    />
                </div>
                <div className="event-card__description">
                    <div className="event-card__description-content" style={gridStyle}>
                        {renderEventDescription()}
                    </div>
                    <div className="event-card__status">
                        {renderEventStatus()}
                        {isStandardSport && renderEventIcons()}
                    </div>
                </div>
                {!isStandardSport && renderEventIcons()}
                <div className="event-card__arrow" />
            </div>
        </Link>
    );
};

EventCard.propTypes = {
    event: PropTypes.object.isRequired,
    gtm: PropTypes.string,
};

export default EventCard;
